import React, { useState, useMemo } from 'react'
import { Bar } from 'react-chartjs-2'
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend
} from 'chart.js'
import {
  Box, Typography, Button, Container, Paper, CircularProgress, Chip
} from '@mui/material'
import { useQuery, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import PaymentsIcon from '@mui/icons-material/Payments'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import HomeIcon from '@mui/icons-material/Home'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const GET_VENDOR_DASHBOARD = gql`
  query GetVendorDashboard($vendor: ID, $startingDate: String, $endingDate: String) {
    getVendorDashboard(vendor: $vendor, startingDate: $startingDate, endingDate: $endingDate) {
      totalOrders
      totalSales
      totalCardOrders
      salesByDay { day amount }
      ordersByDay { day count }
    }
  }
`

const StatCard = ({ label, value, icon, gradient, loading, prefix = '' }) => (
  <Paper elevation={0} sx={{
    background: gradient, borderRadius: '20px', p: 3,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)', flex: 1, minWidth: 160
  }}>
    <Box>
      <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)', textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.5 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '2rem', fontWeight: 800, color: '#fff', lineHeight: 1 }}>
        {loading
          ? <CircularProgress size={22} sx={{ color: 'rgba(255,255,255,0.7)' }} />
          : `${prefix}${value ?? 0}`}
      </Typography>
      <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>All restaurants</Typography>
    </Box>
    <Box sx={{ width: 52, height: 52, borderRadius: '16px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {icon}
    </Box>
  </Paper>
)

const toDateStr = d => d.toISOString().slice(0, 10)

const MerchantHome = () => {
  const userType = (() => { try { return JSON.parse(localStorage.getItem('user-enatega'))?.userType } catch { return null } })()
  const isSuperAdmin = userType === 'ADMIN' || userType === 'SUPER_ADMIN'
  // Super admin in vendor context uses activeVendorId; vendor uses their own id; admin with no context sees all
  const vendorId = isSuperAdmin ? (localStorage.getItem('activeVendorId') || null) : localStorage.getItem('vendorId')

  const today = toDateStr(new Date())
  const initStart = () => { const d = new Date(); d.setDate(d.getDate() - 6); return toDateStr(d) }

  const [startingDate, setStartingDate] = useState(initStart())
  const [endingDate, setEndingDate] = useState(today)
  const [pendingStart, setPendingStart] = useState(initStart())
  const [pendingEnd, setPendingEnd] = useState(today)

  const setQuickRange = days => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - (days - 1))
    const s = toDateStr(start), e = toDateStr(end)
    setPendingStart(s); setPendingEnd(e)
    setStartingDate(s); setEndingDate(e)
  }

  const { data, loading } = useQuery(GET_VENDOR_DASHBOARD, {
    variables: { vendor: vendorId, startingDate, endingDate: endingDate + 'T23:59:59' },
    fetchPolicy: 'network-only'
  })

  const dash = data?.getVendorDashboard
  const totalOrders = dash?.totalOrders ?? 0
  const totalSales = dash?.totalSales ?? 0
  const onlineOrders = dash?.totalCardOrders ?? 0
  const avgOrderValue = totalOrders > 0 ? (Number(totalSales) / totalOrders).toFixed(2) : '0.00'

  const { chartLabels, revenueData, ordersCountData } = useMemo(() => {
    const salesMap = {}
    const ordersMap = {}
    ;(dash?.salesByDay ?? []).forEach(d => { salesMap[d.day] = d.amount })
    ;(dash?.ordersByDay ?? []).forEach(d => { ordersMap[d.day] = d.count })

    const labels = [], revenue = [], ordersCount = []
    const [sy, sm, sd] = startingDate.split('-').map(Number)
    const [ey, em, ed] = endingDate.split('-').map(Number)
    const cur = new Date(sy, sm - 1, sd)
    const last = new Date(ey, em - 1, ed)

    while (cur <= last) {
      const y = cur.getFullYear()
      const m = String(cur.getMonth() + 1).padStart(2, '0')
      const day = String(cur.getDate()).padStart(2, '0')
      const key = `${y}-${m}-${day}`
      labels.push(cur.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }))
      revenue.push(salesMap[key] || 0)
      ordersCount.push(ordersMap[key] || 0)
      cur.setDate(cur.getDate() + 1)
    }
    return { chartLabels: labels, revenueData: revenue, ordersCountData: ordersCount }
  }, [dash, startingDate, endingDate])

  const barData = {
    labels: chartLabels,
    datasets: [
      { label: 'Revenue ($)', data: revenueData, backgroundColor: '#818cf8', borderRadius: 6, borderSkipped: false, yAxisID: 'y' },
      { label: 'Orders', data: ordersCountData, backgroundColor: '#34d399', borderRadius: 6, borderSkipped: false, yAxisID: 'y1' }
    ]
  }

  const barOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top', labels: { color: '#64748b', font: { size: 12 } } },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: '#94a3b8', maxTicksLimit: 10 } },
      y: { grid: { color: '#f1f5f9' }, ticks: { color: '#94a3b8', callback: v => `$${v}` }, position: 'left' },
      y1: { grid: { display: false }, ticks: { color: '#94a3b8' }, position: 'right' }
    }
  }

  const dateStr = new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })

  return (
    <>
      {/* Hero banner */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)',
        px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }
      }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HomeIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15 }}>Home</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>
              {dateStr} &middot; All Restaurants Overview
            </Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        {/* Stat cards */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <StatCard label="Total Orders" value={totalOrders} loading={loading} icon={<ShoppingCartIcon sx={{ color: '#fff', fontSize: 26 }} />} gradient="linear-gradient(135deg, #f7971e, #ffd200)" />
          <StatCard label="Total Revenue" value={Number(totalSales).toFixed(2)} loading={loading} prefix="$" icon={<AttachMoneyIcon sx={{ color: '#fff', fontSize: 26 }} />} gradient="linear-gradient(135deg, #a18cd1, #fbc2eb)" />
          <StatCard label="Online Orders" value={onlineOrders} loading={loading} icon={<PaymentsIcon sx={{ color: '#fff', fontSize: 26 }} />} gradient="linear-gradient(135deg, #4facfe, #00f2fe)" />
          <StatCard label="Avg Order Value" value={avgOrderValue} loading={loading} prefix="$" icon={<TrendingUpIcon sx={{ color: '#fff', fontSize: 26 }} />} gradient="linear-gradient(135deg, #43e97b, #38f9d7)" />
        </Box>

        {/* Date filter */}
        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', p: 3, mb: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#1e293b', mr: 1 }}>Date Range</Typography>
            {[{ label: 'Today', days: 1 }, { label: '7 Days', days: 7 }, { label: '30 Days', days: 30 }].map(q => (
              <Button key={q.days} size="small" variant="outlined" onClick={() => setQuickRange(q.days)}
                sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.8rem', borderColor: '#e2e8f0', color: '#475569', '&:hover': { borderColor: '#818cf8', color: '#818cf8', bgcolor: '#f5f3ff' } }}>
                {q.label}
              </Button>
            ))}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto', flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>From</Typography>
                <input type="date" value={pendingStart} max={pendingEnd} onChange={e => setPendingStart(e.target.value)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 10px', fontSize: '0.82rem', color: '#1e293b', outline: 'none' }} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography sx={{ fontSize: '0.8rem', color: '#64748b', fontWeight: 500 }}>To</Typography>
                <input type="date" value={pendingEnd} min={pendingStart} max={today} onChange={e => setPendingEnd(e.target.value)}
                  style={{ border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 10px', fontSize: '0.82rem', color: '#1e293b', outline: 'none' }} />
              </Box>
              <Button variant="contained" size="small" onClick={() => { setStartingDate(pendingStart); setEndingDate(pendingEnd) }}
                sx={{ borderRadius: '8px', textTransform: 'none', fontSize: '0.82rem', bgcolor: '#818cf8', '&:hover': { bgcolor: '#6366f1' }, boxShadow: 'none', px: 2.5 }}>
                Apply
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Chart */}
        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', p: 3, boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>Revenue & Orders</Typography>
            <Chip label={`${startingDate} → ${endingDate}`} size="small" sx={{ fontSize: '0.72rem', bgcolor: '#f1f5f9', color: '#64748b' }} />
          </Box>
          <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mb: 2.5 }}>
            Combined daily revenue and orders across all your restaurants
          </Typography>
          <Box sx={{ height: 320 }}>
            {loading
              ? <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}><CircularProgress size={32} sx={{ color: '#818cf8' }} /></Box>
              : <Bar data={barData} options={barOptions} />
            }
          </Box>
        </Paper>
      </Container>
    </>
  )
}

export default withTranslation()(MerchantHome)
